import { useState, useEffect, useRef } from 'react';
import { useToast } from '../../components/common';
import { useConfirm } from '../../components/nucleus/actions';
import { plural } from '../../nucleus/format';
import { useAuth } from '../../contexts/AuthContext';
import { useGroup } from '../../contexts/GroupContext';
import api from '../../services/api';
import { encryptValueJson } from '../../utils/fieldEncryption';

/**
 * Custom hook: manages node create/update/delete, modals, context menu, and keyboard shortcuts.
 */
export default function useNodeCRUD({
    graphTab,
    relationships = [],
    loadIdentityGraph,
    catalogMode = false,
}) {
    const { activeGroupId, isGroupContext } = useGroup();
    const { success, error: showError } = useToast();
    const confirm = useConfirm();
    const { encKeys } = useAuth();

    // Every mutation below is a no-op in catalog mode — these nodes belong to
    // an app's own headless account, not to whoever is looking at this
    // preview. This is UX honesty, not the actual backstop: the backend
    // independently refuses every one of these writes regardless (a catalog
    // node's owner_id is never the viewer's), so a bypass here fails loudly
    // server-side rather than mutating anything. The point of gating here is
    // that a control should never look actionable and then silently do
    // nothing — see readOnlyNotice below.
    const readOnlyNotice = () => showError("This is a read-only preview of the app's catalog.");

    // Modal & context menu state
    const [contextMenu, setContextMenu] = useState(null);
    const [selectedNode, setSelectedNode] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showRelationshipModal, setShowRelationshipModal] = useState(false);
    const [multiSelectedNodes, setMultiSelectedNodes] = useState(new Set());

    const refreshCurrentView = async () => {
        if (graphTab === 'database') {
            const ignore = { current: false };
            await loadIdentityGraph({ ignore });
        }
    };

    const handleNodeRightClick = (node, event) => {
        event.preventDefault();
        setSelectedNode(node);
        setContextMenu({ x: event.clientX, y: event.clientY, node });
    };

    const handleNodeClick = (node) => {
        setSelectedNode(node);
    };

    const handleCreateNode = async (formData) => {
        if (catalogMode) return readOnlyNotice();
        try {
            let valueJson = {
                ...(formData.value_json || {}),
                ...(formData.metadata || {}),
            };
            if (formData.value && !valueJson.content) {
                valueJson.content = formData.value;
            }

            // Encrypt encrypted_fields if schema declares them and encKeys is available
            if (formData.schema_id && encKeys?.publicKey) {
                const schema = await api.getSchema(formData.schema_id).catch(() => null);
                if (schema?.encrypted_fields?.length) {
                    valueJson = await encryptValueJson(valueJson, schema.encrypted_fields, encKeys);
                }
            }

            if (isGroupContext && activeGroupId) {
                await api.createGroupNode(activeGroupId, {
                    title: formData.title,
                    value_json: valueJson,
                    ...(formData.schema_id ? { schema_id: formData.schema_id } : {}),
                });
            } else {
                await api.createNode(
                    formData.title,
                    valueJson,
                    formData.schema_id || null
                );
            }
            success('Node created successfully');
            setShowCreateModal(false);
            await refreshCurrentView();
            window.dispatchEvent(new CustomEvent('nodes-changed')); // refresh sidebar Recent
        } catch (err) {
            if (err.code === 'SCHEMA_VALIDATION_ERROR' && err.details?.errors?.length) {
                showError('Schema validation failed:\n' + err.details.errors.join('\n'));
            } else {
                showError('Failed to create node: ' + err.message);
            }
        }
    };

    const handleUpdateNode = async (nodeData) => {
        if (catalogMode) return readOnlyNotice();
        try {
            setSelectedNode(null);

            const updatePayload = {
                title: nodeData.title,
            };

            let valueJson = nodeData.value_json;
            // Encrypt encrypted_fields before update
            if (valueJson && encKeys?.publicKey) {
                const schemaId = nodeData.schema_id;
                if (schemaId) {
                    const schema = await api.getSchema(schemaId).catch(() => null);
                    if (schema?.encrypted_fields?.length) {
                        valueJson = await encryptValueJson(valueJson, schema.encrypted_fields, encKeys);
                    }
                }
            }

            if (valueJson) updatePayload.value_json = valueJson;
            if (nodeData.schema_id) updatePayload.schema_id = nodeData.schema_id;
            if (isGroupContext && activeGroupId) {
                await api.updateGroupNode(activeGroupId, nodeData.id, updatePayload);
            } else {
                await api.updateNode(nodeData.id, updatePayload);
            }
            success('Node updated successfully');
            await refreshCurrentView();
        } catch (err) {
            if (err.code === 'SCHEMA_VALIDATION_ERROR' && err.details?.errors?.length) {
                showError('Schema validation failed:\n' + err.details.errors.join('\n'));
            } else {
                showError('Failed to update node: ' + err.message);
            }
        }
    };

    // DELETING A NODE asks first, in the one are-you-sure dialog (actions.js),
    // saying how many links go with it; a second Delete while it asks is ignored
    const asking = useRef(false);
    const handleDeleteNode = async (node) => {
        if (catalogMode) return readOnlyNotice();
        if (asking.current) return;
        const links = relationships.filter((r) => [r.from_node_id, r.to_node_id, r.source, r.target].includes(node.id)).length;
        asking.current = true;
        let ok;
        try {
            ok = await confirm({
                title: `Delete “${node.title || node.label || 'Untitled'}”?`,
                body: `${links === 1 ? 'Its one link goes with it. ' : links ? `Its ${plural(links, 'link')} go with it. ` : ''}This cannot be undone.`,
                label: 'Delete',
            });
        } finally { asking.current = false; }
        if (!ok) return;
        try {
            // Group nodes live under the group scope; the personal /nodes route only
            // matches nodes you own, so deleting a group node there 404s.
            if (isGroupContext && activeGroupId) {
                await api.deleteGroupNode(activeGroupId, node.id);
            } else {
                await api.deleteNode(node.id);
            }
            success('Node deleted successfully');
            setSelectedNode(null);
            await refreshCurrentView();
        } catch (err) {
            showError('Failed to delete node: ' + (err.response?.data?.error?.message || err.message));
        }
    };

    const handleAddRelationshipSuccess = async () => {
        if (catalogMode) return readOnlyNotice();
        success('Relationship added successfully');
        setShowRelationshipModal(false);
        await new Promise(resolve => setTimeout(resolve, 100));
        await refreshCurrentView();
    };

    const handleBulkDelete = async () => {
        if (catalogMode) return readOnlyNotice();
        const ok = await confirm({ title: `Delete ${plural(multiSelectedNodes.size, 'selected node')}?`, body: 'This cannot be undone.', label: 'Delete' });
        if (!ok) return;
        try {
            const ids = Array.from(multiSelectedNodes);
            if (isGroupContext && activeGroupId) {
                await api.bulkDeleteGroupNodes(activeGroupId, ids);
            } else {
                await api.bulkDeleteNodes(ids);
            }
            success(`Deleted ${multiSelectedNodes.size} node(s)`);
            setMultiSelectedNodes(new Set());
            const ignore = { current: false };
            await loadIdentityGraph({ ignore });
        } catch (err) {
            showError('Failed to delete nodes: ' + (err.message || 'Unknown error'));
        }
    };

    // Keyboard shortcuts. Skipped entirely in catalog mode — Cmd/Ctrl+N opens
    // the create modal directly (bypassing handleCreateNode's own guard
    // above), so this listener is the one mutation trigger a prop on
    // DatabaseTableView could never reach; it has to be gated at the effect's
    // own boundary instead.
    useEffect(() => {
        if (catalogMode) return undefined;
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
                e.preventDefault();
                setShowCreateModal(true);
            }
            if (e.key === 'Escape') {
                setShowCreateModal(false);
                setContextMenu(null);
            }
            if (e.key === 'Delete' && selectedNode) {
                handleDeleteNode(selectedNode);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedNode, catalogMode]);

    const handleRatifyNode = async (node) => {
        if (catalogMode) return readOnlyNotice();
        try {
            await api.ratifyNode(node.id);
            success('Node ratified — now declared');
            await refreshCurrentView();
        } catch (err) {
            showError('Failed to ratify: ' + err.message);
        }
    };

    const handleUnratifyNode = async (node) => {
        if (catalogMode) return readOnlyNotice();
        try {
            await api.unratifyNode(node.id);
            success('Node unratified — back to inferred');
            await refreshCurrentView();
        } catch (err) {
            showError('Failed to unratify: ' + err.message);
        }
    };

    return {
        contextMenu, setContextMenu,
        selectedNode, setSelectedNode,
        showCreateModal, setShowCreateModal,
        showRelationshipModal, setShowRelationshipModal,
        multiSelectedNodes, setMultiSelectedNodes,
        handleNodeRightClick, handleNodeClick,
        handleCreateNode, handleUpdateNode, handleDeleteNode,
        handleAddRelationshipSuccess, handleBulkDelete,
        handleRatifyNode, handleUnratifyNode,
    };
}
